import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Users, Search, Mail, Phone, IdCard, Pencil, Trash2, Loader2,
  RefreshCw, MailCheck, MailWarning, CheckCircle2, XCircle, Send, GraduationCap
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PAGE_SIZE = 50;

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  } catch {
    return iso;
  }
}

export default function AdminUsuariosExternosPage() {
  const token = localStorage.getItem('token');
  const [usuarios, setUsuarios] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [skip, setSkip] = useState(0);

  // Filtros
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [estadoFilter, setEstadoFilter] = useState('todos');
  const [verifFilter, setVerifFilter] = useState('todos');

  // Modal de edición
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Confirmación de borrado
  const [deleting, setDeleting] = useState(null);
  const [deletingBusy, setDeletingBusy] = useState(false);

  // Reenviar verificación
  const [reenviando, setReenviando] = useState(null);

  const fetchUsuarios = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip, limit: PAGE_SIZE };
      if (search) params.search = search;
      if (estadoFilter !== 'todos') params.estado = estadoFilter;
      if (verifFilter === 'verificados') params.verificado = true;
      if (verifFilter === 'pendientes') params.verificado = false;

      const res = await axios.get(`${API_URL}/campus/admin/externos`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setUsuarios(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('Error listando externos:', err);
      toast.error(err.response?.data?.detail || 'No pudimos cargar la lista de usuarios externos.');
    } finally {
      setLoading(false);
    }
  }, [token, skip, search, estadoFilter, verifFilter]);

  useEffect(() => {
    fetchUsuarios();
  }, [fetchUsuarios]);

  const onSubmitSearch = (e) => {
    e.preventDefault();
    setSkip(0);
    setSearch(searchInput.trim());
  };

  const limpiarFiltros = () => {
    setSearchInput('');
    setSearch('');
    setEstadoFilter('todos');
    setVerifFilter('todos');
    setSkip(0);
  };

  const abrirEdicion = (u) => {
    setEditing(u);
    setEditForm({
      nombre: u.nombre || '',
      apellido: u.apellido || '',
      email: u.email || '',
      dni: u.dni || '',
      telefono: u.telefono || '',
      estado: u.estado || 'activo',
      email_verificado: !!u.email_verificado,
    });
  };

  const cerrarEdicion = () => {
    setEditing(null);
    setEditForm({});
  };

  const guardarEdicion = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      // Solo enviamos campos modificados
      const payload = {};
      ['nombre', 'apellido', 'email', 'dni', 'telefono', 'estado'].forEach((k) => {
        if (editForm[k] !== (editing[k] ?? '')) payload[k] = editForm[k];
      });
      if (editForm.email_verificado !== !!editing.email_verificado) {
        payload.email_verificado = editForm.email_verificado;
      }

      if (Object.keys(payload).length === 0) {
        toast.info('No hay cambios para guardar.');
        setSaving(false);
        return;
      }

      await axios.put(`${API_URL}/campus/admin/externos/${editing.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Usuario actualizado correctamente.');
      cerrarEdicion();
      fetchUsuarios();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'No pudimos guardar los cambios.');
    } finally {
      setSaving(false);
    }
  };

  const confirmarEliminar = async () => {
    if (!deleting) return;
    setDeletingBusy(true);
    try {
      const res = await axios.delete(`${API_URL}/campus/admin/externos/${deleting.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const memb = res.data?.membresias_eliminadas || 0;
      toast.success(
        memb > 0
          ? `Usuario eliminado. Se borraron ${memb} membresía(s) del Campus.`
          : 'Usuario eliminado.'
      );
      setDeleting(null);
      fetchUsuarios();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'No pudimos eliminar el usuario.');
    } finally {
      setDeletingBusy(false);
    }
  };

  const reenviarVerificacion = async (u) => {
    setReenviando(u.id);
    try {
      await axios.post(
        `${API_URL}/campus/admin/externos/${u.id}/reenviar-verificacion`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Email de verificación reenviado a ${u.email}.`);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'No se pudo reenviar el email.');
    } finally {
      setReenviando(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(skip / PAGE_SIZE) + 1;

  return (
    <div className="p-6 space-y-6" data-testid="admin-usuarios-externos-page">
      <Toaster position="top-center" richColors />

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <GraduationCap className="w-6 h-6 text-primary" />
            Usuarios Externos del Campus
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Personas no matriculadas que crearon una cuenta para acceder al Campus CATSLZ.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => fetchUsuarios()}
          data-testid="refresh-externos-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualizar
        </Button>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={onSubmitSearch} className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[220px]">
              <Label htmlFor="search-externos" className="text-xs text-gray-500">
                Buscar (email, nombre, DNI, teléfono)
              </Label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  id="search-externos"
                  data-testid="search-externos-input"
                  className="pl-9"
                  placeholder="ej: maria@gmail.com o 30123456"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </div>
            </div>

            <div>
              <Label className="text-xs text-gray-500">Estado</Label>
              <Select value={estadoFilter} onValueChange={(v) => { setEstadoFilter(v); setSkip(0); }}>
                <SelectTrigger className="w-40" data-testid="estado-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="activo">Activos</SelectItem>
                  <SelectItem value="inactivo">Inactivos</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs text-gray-500">Verificación</Label>
              <Select value={verifFilter} onValueChange={(v) => { setVerifFilter(v); setSkip(0); }}>
                <SelectTrigger className="w-44" data-testid="verif-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="verificados">Verificados</SelectItem>
                  <SelectItem value="pendientes">Pendientes</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button type="submit" data-testid="buscar-externos-btn">
              <Search className="w-4 h-4 mr-2" />
              Buscar
            </Button>
            <Button type="button" variant="ghost" onClick={limpiarFiltros}>
              Limpiar
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Tabla */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="w-4 h-4" />
            {loading ? 'Cargando…' : `${total} usuario(s) externo(s)`}
          </CardTitle>
          <CardDescription>
            Página {currentPage} de {totalPages}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : usuarios.length === 0 ? (
            <div className="text-center py-16 text-gray-500" data-testid="externos-empty">
              No se encontraron usuarios externos con esos filtros.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr className="text-left">
                    <th className="px-3 py-2 font-medium text-gray-600">Nombre</th>
                    <th className="px-3 py-2 font-medium text-gray-600">Contacto</th>
                    <th className="px-3 py-2 font-medium text-gray-600">DNI</th>
                    <th className="px-3 py-2 font-medium text-gray-600">Estado</th>
                    <th className="px-3 py-2 font-medium text-gray-600">Verificación</th>
                    <th className="px-3 py-2 font-medium text-gray-600">Espacios</th>
                    <th className="px-3 py-2 font-medium text-gray-600">Registro</th>
                    <th className="px-3 py-2 font-medium text-gray-600 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {usuarios.map((u) => (
                    <tr
                      key={u.id}
                      className="border-b hover:bg-gray-50"
                      data-testid={`externo-row-${u.id}`}
                    >
                      <td className="px-3 py-3">
                        <div className="font-medium text-gray-900">
                          {u.nombre} {u.apellido}
                        </div>
                        <div className="text-xs text-gray-500">ID: {u.id?.slice(0, 8)}…</div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-1 text-gray-700">
                          <Mail className="w-3 h-3 text-gray-400" />
                          <span className="break-all">{u.email}</span>
                        </div>
                        {u.telefono && (
                          <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                            <Phone className="w-3 h-3" />
                            {u.telefono}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-1 text-gray-700">
                          <IdCard className="w-3 h-3 text-gray-400" />
                          {u.dni || '—'}
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        {u.estado === 'activo' ? (
                          <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Activo
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="bg-gray-200 text-gray-700">
                            <XCircle className="w-3 h-3 mr-1" /> Inactivo
                          </Badge>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        {u.email_verificado ? (
                          <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">
                            <MailCheck className="w-3 h-3 mr-1" /> Verificado
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">
                            <MailWarning className="w-3 h-3 mr-1" /> Pendiente
                          </Badge>
                        )}
                      </td>
                      <td className="px-3 py-3 text-center text-gray-700">
                        {u.membresias_count || 0}
                      </td>
                      <td className="px-3 py-3 text-xs text-gray-500">
                        {formatDate(u.created_at)}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {!u.email_verificado && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => reenviarVerificacion(u)}
                              disabled={reenviando === u.id}
                              data-testid={`reenviar-verif-${u.id}`}
                              title="Reenviar email de verificación"
                            >
                              {reenviando === u.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Send className="w-4 h-4" />
                              )}
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => abrirEdicion(u)}
                            data-testid={`editar-externo-${u.id}`}
                            title="Editar"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleting(u)}
                            data-testid={`eliminar-externo-${u.id}`}
                            title="Eliminar"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Paginación */}
          {!loading && total > PAGE_SIZE && (
            <div className="flex items-center justify-between mt-4">
              <div className="text-xs text-gray-500">
                Mostrando {skip + 1}–{Math.min(skip + PAGE_SIZE, total)} de {total}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={skip === 0}
                  onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
                  data-testid="page-prev-btn"
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={skip + PAGE_SIZE >= total}
                  onClick={() => setSkip(skip + PAGE_SIZE)}
                  data-testid="page-next-btn"
                >
                  Siguiente
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de edición */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && cerrarEdicion()}>
        <DialogContent className="max-w-lg" data-testid="editar-externo-modal">
          <DialogHeader>
            <DialogTitle>Editar usuario externo</DialogTitle>
            <DialogDescription>
              Corregí datos personales o cambiá el estado de la cuenta.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="edit-nombre">Nombre</Label>
              <Input
                id="edit-nombre"
                data-testid="edit-nombre-input"
                value={editForm.nombre || ''}
                onChange={(e) => setEditForm({ ...editForm, nombre: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="edit-apellido">Apellido</Label>
              <Input
                id="edit-apellido"
                data-testid="edit-apellido-input"
                value={editForm.apellido || ''}
                onChange={(e) => setEditForm({ ...editForm, apellido: e.target.value })}
              />
            </div>
            <div className="col-span-2">
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                data-testid="edit-email-input"
                value={editForm.email || ''}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="edit-dni">DNI</Label>
              <Input
                id="edit-dni"
                data-testid="edit-dni-input"
                value={editForm.dni || ''}
                onChange={(e) => setEditForm({ ...editForm, dni: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="edit-telefono">Teléfono</Label>
              <Input
                id="edit-telefono"
                data-testid="edit-telefono-input"
                value={editForm.telefono || ''}
                onChange={(e) => setEditForm({ ...editForm, telefono: e.target.value })}
              />
            </div>
            <div>
              <Label>Estado</Label>
              <Select
                value={editForm.estado || 'activo'}
                onValueChange={(v) => setEditForm({ ...editForm, estado: v })}
              >
                <SelectTrigger data-testid="edit-estado-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="activo">Activo</SelectItem>
                  <SelectItem value="inactivo">Inactivo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Email verificado</Label>
              <Select
                value={editForm.email_verificado ? 'si' : 'no'}
                onValueChange={(v) => setEditForm({ ...editForm, email_verificado: v === 'si' })}
              >
                <SelectTrigger data-testid="edit-verificado-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="si">Sí (verificado)</SelectItem>
                  <SelectItem value="no">No (pendiente)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={cerrarEdicion} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={guardarEdicion} disabled={saving} data-testid="guardar-externo-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Guardar cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmación de borrado */}
      <Dialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <DialogContent data-testid="eliminar-externo-modal">
          <DialogHeader>
            <DialogTitle>¿Eliminar este usuario externo?</DialogTitle>
            <DialogDescription>
              Se eliminará la cuenta de{' '}
              <strong>
                {deleting?.nombre} {deleting?.apellido}
              </strong>{' '}
              ({deleting?.email}) y todas sus membresías en espacios del Campus.
              Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleting(null)} disabled={deletingBusy}>
              Cancelar
            </Button>
            <Button
              onClick={confirmarEliminar}
              disabled={deletingBusy}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirmar-eliminar-externo-btn"
            >
              {deletingBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
